import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { KeycloakService } from 'keycloak-angular';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { LicenceTypeId } from '../../entities/payment/payment';
import { Role } from '../../entities/role/role';
import { User, UserActions, UserInput } from '../../entities/user/user';
import { ApolloService } from '../apollo.service';
import * as fromGraphQl from './graphql';

/**
 * The users service.
 */
@Injectable()
export class UsersService {

  public theme = 'standard';
  private renderer: Renderer2;
  constructor(
    private apolloService: ApolloService,
    private apollo: Apollo,
    private keycloakAngular: KeycloakService,
    rendererFactory: RendererFactory2,
    private translateService: TranslateService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }
  /**
   * suspends an user
   * @param userId id
   * @param refetchQuery QueryRef
   */
  public suspendUser(userId: string, callback: (arg: any) => void): any {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DISABLE_USER_MUTATION,
      {
        userId,
      },
      callback
    );
  }
  /**
   * unsuspends an user
   * @param userId id
   * @param refetchQuery QueryRef
   */
  public enableUser(userId: string, callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.ENABLE_USER_MUTATION,
      {
        userId,
      },
      callback
    );
  }
  /**
   * updates the roles of an user
   * @param userInput user Input
   * @param refetchQuery QueryRef
   */
  public updateRoles(userInput: UserInput, refetchQuery: QueryRef<any>): void {
    this.apolloService.mutate(
      fromGraphQl.UPDATE_AUTHORITIES_MUTATION,
      {
        user: userInput,
      },
      refetchQuery
    );
  }
  /**
   * checks if an email address is already taken
   * @param email email address
   */
  public checkEmailNotTaken(email: string): Observable<boolean> {
    return this.apollo
      .mutate({
        mutation: fromGraphQl.CHECK_EMAIL_MUTATION,
        variables: {
          email,
        },
      })
      .pipe(map((result: any) => result.data.checkEmailAddress));
  }
  /**
   * updates an user
   * @param userInput user Input
   * @param callback method
   */
  public updateUser(userInput: UserInput, callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_USER_MUTATION,
      {
        input: userInput,
      },
      callback
    );
  }
  /**
   * creates an user
   * @param userInput user Inout
   * @param callback method
   */
  public createUser(userInput: UserInput, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.CREATE_USER_MUTATION,
      {
        input: userInput,
      },
      callback
    );
  }
  /**
   * resets Password
   * @param id user Id
   * @param passwod kennwort
   * @param callback method
   */
  public resetPassword(
    id: string,
    password: string,
    requiredActions: string[],
    callback: (arg: any) => void
  ): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.RESET_PASSWORD_MUTATION,
      {
        id,
        password,
        requiredActions
      },
      callback
    );
  }
  /**
   * updates an user
   * @param userInput user Input
   * @param refetchQuery QueryRef
   */
  public changeSite(userInput: UserInput, refetchQuery: QueryRef<any>): void {
    this.apolloService.mutate(
      fromGraphQl.CHANGE_SITE_MUTATION,
      {
        user: userInput,
      },
      refetchQuery
    );
  }
  /**
   * returns the currently used/active tenantId of the logged-in user
   * @returns string
   */
  public getCurrentTenantId(): string | undefined {
    const key = 'tenant_id';
    if (
      this.keycloakAngular.getKeycloakInstance() &&
      this.keycloakAngular.getKeycloakInstance().tokenParsed
    ) {
      const parsedToken = this.keycloakAngular.getKeycloakInstance()
        .tokenParsed as any;
      return parsedToken[key];
    }
    return undefined;
  }

    /**
   * returns the user ID of the currently logged in user
   * @returns string
   */
  public getCurrentUserId(): string | undefined {
    const key = 'id';
    if (
      this.keycloakAngular.getKeycloakInstance() &&
      this.keycloakAngular.getKeycloakInstance().tokenParsed
    ) {
      const parsedToken = this.keycloakAngular.getKeycloakInstance()
        .tokenParsed as any;
      return parsedToken[key];
    }
    return undefined;
  }

  /**
   * returns the licence type of the currently logged in user
   * @returns string the licence type, or NO_LICENCE, if the user has no licence or the type is known
   */
  public getCurrentLicenceType(): string | undefined {
    const key = 'licence_type';
    if (
      this.keycloakAngular.getKeycloakInstance() &&
      this.keycloakAngular.getKeycloakInstance().tokenParsed
    ) {
      const parsedToken = this.keycloakAngular.getKeycloakInstance()
        .tokenParsed as any;
      if(parsedToken['realm_access']?.roles?.includes('framework_licencing_full_user')){
        return 'FULL_USER';
      }
      if(parsedToken['realm_access']?.roles?.includes('framework_licencing_app_user')){
        return "APP_USER";
      }
    }
    return "NO_LICENCE";
  }



  /**
   * checks, if the currently logged in user has the role 'framework_nexato_employee'
   * @returns true, if the currently logged in user has the role 'framework_nexato_employee', false otherwise
   */
  public isCurrentUserNexatoEmployee(): boolean {
    if (
      this.keycloakAngular.getKeycloakInstance() &&
      this.keycloakAngular.getKeycloakInstance().tokenParsed
    ) {
      const parsedToken = this.keycloakAngular.getKeycloakInstance()
        .tokenParsed as any;
      return parsedToken['realm_access']?.roles?.includes('framework_nexato_employee');
    }
    return false;
  }

  public isNexatoEmployee(user: User): boolean {
    return user.roles.find(role => role.name === 'framework_nexato_employee') !== undefined;
  }

  /**
   * updates an user
   * @param tenant
   * returns boolean
   */
  public switchTenant(tenant: string): void {
    this.apollo
      .mutate({
        mutation: fromGraphQl.SWITCH_TENANT_MUTATION,
        variables: {
          tenant,
        },
      })
      .subscribe(
        ({ }) => {
          this.keycloakAngular.updateToken(-1).then(function(success): void {
            window.location.reload();
          });
        },
        (error) => {
          console.log('there was an error sending the query', error);
        }
      );
  }

   /**
   * setRequiredActions
   * @param id user Id
   * @param requiredActions UPDATE_PASSWORD
   * @param callback method
   */
    public setRequiredActions(
      id: string,
      requiredActions: UserActions[],
      callback: (arg: any) => void
    ): void {
      this.apolloService.mutateAndExecute(
        fromGraphQl.SET_REQUIRED_ACTIONS,
        {
          id,
          requiredActions
        },
        callback
      );
    }
  /**
   * updtaes LicenceType
   * @returns string
   */
  public updateLicenceType(userId: string, licenceType: LicenceTypeId, callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_LICENCE_TYPE,
      {
        userId,
        licenceType
      },
      callback
    );
  }

  addRoles(user: User, result: Role[], callback: () => void) {
    let input = UserInput.createUserInputFromUser(user);
    // merge the existing roles with the new ones
    console.log('old', user);
    let roles = user.roles;
    for (let i = 0; i < result.length; i++) {
      roles.push(result[i]);
    }
    input.roles = roles.map((role) => role.id);
    console.log('input', input);
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_USER_MUTATION,
      {
        input: input
      },
      callback
    );
  }

  removeRoles(user: User, result: Role[], callback: () => void) {
    let input = UserInput.createUserInputFromUser(user);
    // remove the roles
    let roles = user.roles;
    for (let i = 0; i < result.length; i++) {
      roles = roles.filter((role) => role.id !== result[i].id);
    }
    input.roles = roles.map((role) => role.id);
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_USER_MUTATION,
      {
        input: input
      },
      callback
    );
  }

  /**
   * Updates the language of the current user. Currently a dummy, that stores
   * the languagre in the local storage
   * 
   * @param language 
   * @param callback 
   */
  updatePreferredLanguage(language: string, callback: (arg: any) => void): void {
    // for now we´re usind local storage to store the language
    let userId = this.getCurrentUserId();
    if(userId && language) {
      this.translateService.use(language);
      localStorage.setItem(userId + '_language', language);
    } else if (userId) {
      localStorage.removeItem(userId + '_language');
    }
    let result = true;
    callback(result);
  }

  getCurrentUsersPreferredlanguage(): string | undefined {
    let userId = this.getCurrentUserId();
    if(userId) {
      let language = localStorage.getItem(userId + '_language');
      if(language) {
        return language;
      }
    }
    return undefined;
  }

  public getUsers(): Observable<User[]> {
    return this.apolloService.query<fromGraphQl.GetUsersResponse>(fromGraphQl.GET_USERS_QUERY)
      .pipe(
        take(1),
        switchMap( (result) => {
          console.log(result);
          return of( User.createUsers(result.data.getUsers));
        })
      );
    return of(undefined);
  }

}
